'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Compass, Smartphone, Sparkles } from 'lucide-react';
import {
  FIRST_INSIGHT_BY_OBSTACLE,
  INSIGHT_AFTER,
  NAME_QUESTION,
  QUESTIONS,
  SECOND_INSIGHT,
  THIRD_INSIGHT,
  TOTAL_QUESTIONS,
  obstacleFromAnswers,
  type InsightScreen,
  type InsightScreenNumber,
  type QuizQuestion,
} from '@timeblock/assessment-content/assessments/follow-through-plan';
import { useAssessmentHeaderContext } from '@/contexts/AssessmentHeaderContext';
import { metaPixelEvents, generateEventId } from '@/lib/metaPixel';
import { FUNNEL_PATH, trackFunnel, withBasePath } from '@/lib/follow-through/client';
import { useFollowThroughHydrated, useFollowThroughStore } from '@/lib/follow-through/store';
import { Card, CtaButton, Eyebrow, FunnelShell, OptionButton, ProgressBar, Stars } from '@/components/follow-through/ui';

type Step =
  | { kind: 'question'; question: QuizQuestion; number: number }
  | { kind: 'insight'; screen: InsightScreenNumber }
  | { kind: 'name'; number: number };

export function buildSteps(): Step[] {
  const steps: Step[] = [];
  QUESTIONS.forEach((question, index) => {
    steps.push({ kind: 'question', question, number: index + 1 });
    const insight = (Object.entries(INSIGHT_AFTER) as [string, string][]).find(([, id]) => id === question.id);
    if (insight) steps.push({ kind: 'insight', screen: Number(insight[0]) as InsightScreenNumber });
  });
  steps.push({ kind: 'name', number: TOTAL_QUESTIONS });
  return steps;
}

const STEPS = buildSteps();
const ADVANCE_DELAY_MS = 220;
const INSIGHT_ICONS = { 1: Compass, 2: Sparkles, 3: Smartphone } as const;

export default function QuizClient() {
  const router = useRouter();
  const hydrated = useFollowThroughHydrated();
  const { answers, stepIndex, completed, name, setAnswer, setStepIndex, complete } = useFollowThroughStore();
  const { setHeaderState } = useAssessmentHeaderContext();
  const startedTracked = useRef(false);
  const step = STEPS[Math.min(stepIndex, STEPS.length - 1)];

  useEffect(() => {
    if (hydrated && completed) router.replace(`${FUNNEL_PATH}/plan`);
  }, [hydrated, completed, router]);

  useEffect(() => {
    if (!hydrated || startedTracked.current || stepIndex !== 0) return;
    startedTracked.current = true;
    trackFunnel('minitools_assessment_started', {
      minitools_assessment_url: window.location.href,
      minitools_assessment_title: 'Follow-Through Plan',
    });
    metaPixelEvents.trackViewContent('follow-through-plan', 'Follow-Through Plan', generateEventId());
  }, [hydrated, stepIndex]);

  useEffect(() => {
    setHeaderState({
      showBackButton: stepIndex > 0,
      onBackClick: () => setStepIndex(Math.max(0, stepIndex - 1)),
      isPending: false,
    });
  }, [stepIndex, setHeaderState, setStepIndex]);

  // The header outlives this page; the plan and pricing pages must not inherit the quiz's Back.
  useEffect(
    () => () => setHeaderState({ showBackButton: false, onBackClick: () => {}, isPending: false }),
    [setHeaderState]
  );

  useEffect(() => {
    if (step.kind === 'insight') trackFunnel('minitools_insight_screen_viewed', { screen: step.screen });
    window.scrollTo({ top: 0 });
  }, [step]);

  const goNext = () => {
    const next = stepIndex + 1;
    setStepIndex(next);
    trackFunnel('minitools_assessment_next_step', {
      step: next,
      question_id: step.kind === 'question' ? step.question.id : `insight_${step.kind === 'insight' ? step.screen : 'name'}`,
    });
    metaPixelEvents.trackAssessmentStep('follow-through-plan', next, STEPS.length, generateEventId());
  };

  // One tap moves on; a second tap during the short delay must not skip a question.
  const advancing = useRef(false);
  useEffect(() => {
    advancing.current = false;
  }, [stepIndex]);
  const answerAndAdvance = (questionId: QuizQuestion['id'], value: string | string[]) => {
    setAnswer(questionId, value);
    if (advancing.current) return;
    advancing.current = true;
    setTimeout(goNext, ADVANCE_DELAY_MS);
  };

  const submitName = (firstName: string) => {
    complete(firstName);
    trackFunnel('minitools_assessment_submitted', { minitools_assessment_url: window.location.href });
    router.push(`${FUNNEL_PATH}/plan`);
  };

  if (!hydrated || completed) return <FunnelShell>{null}</FunnelShell>;

  const progress = stepIndex / (STEPS.length - 1);

  return (
    <FunnelShell>
      {/* The header's Back is phone-only (AssessmentHeader), so wider screens get their own. */}
      {stepIndex > 0 && (
        <button
          type="button"
          onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
          className="mb-2 hidden min-h-[44px] items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 sm:flex"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
      )}
      {stepIndex > 0 && <ProgressBar value={progress} />}
      {stepIndex === 0 && step.kind === 'question' ? (
        <Landing question={step.question} onAnswer={(value) => answerAndAdvance(step.question.id, value)} selected={answers.goal} />
      ) : step.kind === 'question' ? (
        <QuestionScreen
          key={step.question.id}
          question={step.question}
          number={step.number}
          value={answers[step.question.id]}
          onAnswer={(value) =>
            step.question.multiSelect ? setAnswer(step.question.id, value) : answerAndAdvance(step.question.id, value)
          }
          onContinue={goNext}
        />
      ) : step.kind === 'insight' ? (
        <InsightScreenView screen={insightFor(step.screen, answers)} number={step.screen} onContinue={goNext} />
      ) : (
        <NameScreen number={step.number} initialName={name} onSubmit={submitName} />
      )}
    </FunnelShell>
  );
}

function insightFor(screen: InsightScreenNumber, answers: Parameters<typeof obstacleFromAnswers>[0]): InsightScreen {
  if (screen === 1) return FIRST_INSIGHT_BY_OBSTACLE[obstacleFromAnswers(answers)];
  return screen === 2 ? SECOND_INSIGHT : THIRD_INSIGHT;
}

function QuestionHeader({ question, number }: { question: QuizQuestion; number: number }) {
  return (
    <>
      <p className="text-sm text-gray-600">
        Question {number} of {TOTAL_QUESTIONS}
      </p>
      <h1 className="mt-2 font-serif text-2xl font-bold leading-snug text-gray-900">{question.title}</h1>
      {question.subtitle && <p className="mt-2 text-base text-gray-600">{question.subtitle}</p>}
    </>
  );
}

function Options({
  question,
  value,
  onAnswer,
}: {
  question: QuizQuestion;
  value: string | string[] | undefined;
  onAnswer: (value: string | string[]) => void;
}) {
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  return (
    <div className="mt-5 space-y-3" role={question.multiSelect ? 'group' : 'radiogroup'} aria-label={question.title}>
      {question.options.map((option) => (
        <OptionButton
          key={option.value}
          label={option.label}
          icon={option.icon}
          multiSelect={question.multiSelect}
          selected={selected.includes(option.value)}
          onClick={() =>
            onAnswer(
              question.multiSelect
                ? selected.includes(option.value)
                  ? selected.filter((v) => v !== option.value)
                  : [...selected, option.value]
                : option.value
            )
          }
        />
      ))}
    </div>
  );
}

function Landing({
  question,
  selected,
  onAnswer,
}: {
  question: QuizQuestion;
  selected: string | string[] | undefined;
  onAnswer: (value: string | string[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <Stars />
        <p className="mt-2 text-sm font-semibold text-[#2A6354]">From the founder of LifeHack</p>
        <h1 className="mt-3 text-balance font-serif text-3xl font-bold leading-tight text-gray-900">Find the pattern keeping you stuck</h1>
        <p className="mt-3 text-base text-gray-700">
          LifeHack uses your answers to build an AI coach around what you are actually dealing with.
        </p>
      </div>

      <Card className="border-t-4 border-[#2A6354] text-center">
        <Eyebrow>Exclusive access</Eyebrow>
        <p className="mt-2 text-lg font-semibold text-gray-900">Try for $0.00</p>
        <p className="text-sm text-gray-600">Start after your personalized plan.</p>
      </Card>

      <Card>
        <QuestionHeader question={question} number={1} />
        <Options question={question} value={selected} onAnswer={onAnswer} />
      </Card>

      <Card>
        <div className="flex items-center gap-4">
          <Image
            src={withBasePath('/lh-logo-round.png')}
            alt="LifeHack"
            width={64}
            height={64}
            className="h-16 w-16 shrink-0 rounded-xl"
          />
          <div>
            <p className="font-semibold text-gray-900">Created by Leon Ho</p>
            <p className="text-sm text-gray-600">Founder of LifeHack</p>
          </div>
        </div>
        <h2 className="mt-5 font-semibold text-gray-900">How your answers personalize your AI coach</h2>
        <ul className="mt-3 space-y-2 text-sm text-gray-700">
          <li>1. Your answers set your starting point and the first week of your plan.</li>
          <li>2. Your coach starts every conversation already knowing what you told us.</li>
          <li>3. Daily check-ins adjust the plan to what actually happens in your week.</li>
        </ul>
      </Card>

      <p className="text-center text-sm text-gray-600">Takes 3 minutes · Trial offer inside</p>
    </div>
  );
}

function QuestionScreen({
  question,
  number,
  value,
  onAnswer,
  onContinue,
}: {
  question: QuizQuestion;
  number: number;
  value: string | string[] | undefined;
  onAnswer: (value: string | string[]) => void;
  onContinue: () => void;
}) {
  const hasSelection = Array.isArray(value) ? value.length > 0 : Boolean(value);
  return (
    <div className="pt-10">
      <QuestionHeader question={question} number={number} />
      <Options question={question} value={value} onAnswer={onAnswer} />
      {question.multiSelect && (
        <CtaButton className="mt-6" disabled={!hasSelection} onClick={onContinue} arrow>
          Continue
        </CtaButton>
      )}
    </div>
  );
}

function InsightScreenView({ screen, number, onContinue }: { screen: InsightScreen; number: InsightScreenNumber; onContinue: () => void }) {
  const Icon = INSIGHT_ICONS[number];
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center pt-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white ring-1 ring-black/10">
        <Icon className="h-7 w-7 text-[#2A6354]" aria-hidden="true" />
      </div>
      <h1 className="mt-6 font-serif text-2xl font-bold leading-snug text-gray-900">{screen.title}</h1>
      {screen.body && <p className="mt-4 text-base leading-relaxed text-gray-700">{screen.body}</p>}
      {screen.points && (
        <ol className="mt-5 space-y-3 text-left">
          {screen.points.map((point, index) => (
            <li key={point} className="flex gap-3 rounded-xl bg-white p-4 text-[15px] text-gray-800 ring-1 ring-black/[0.08]">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2A6354] text-sm font-bold text-white">
                {index + 1}
              </span>
              {point}
            </li>
          ))}
        </ol>
      )}
      {screen.footnote && <p className="mt-4 text-sm font-medium text-[#2A6354]">{screen.footnote}</p>}
      <CtaButton className="mt-8" onClick={onContinue} arrow>
        Continue
      </CtaButton>
    </div>
  );
}

function NameScreen({ number, initialName, onSubmit }: { number: number; initialName: string; onSubmit: (name: string) => void }) {
  const [value, setValue] = useState(initialName);
  const trimmed = useMemo(() => value.trim(), [value]);
  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (trimmed) onSubmit(trimmed);
  };
  return (
    <form className="pt-10" onSubmit={handleSubmit}>
      <p className="text-sm text-gray-600">
        Question {number} of {TOTAL_QUESTIONS}
      </p>
      <h1 className="mt-2 font-serif text-2xl font-bold leading-snug text-gray-900">{NAME_QUESTION.title}</h1>
      <p className="mt-2 text-base text-gray-600">{NAME_QUESTION.subtitle}</p>
      <label htmlFor="ftp-name" className="sr-only">
        First name
      </label>
      <input
        id="ftp-name"
        autoFocus
        autoComplete="given-name"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={NAME_QUESTION.placeholder}
        maxLength={60}
        className="mt-5 h-14 w-full rounded-xl bg-white px-4 text-lg ring-1 ring-black/[0.65] focus:outline-none focus:ring-2 focus:ring-[#2A6354]"
      />
      <CtaButton type="submit" className="mt-6" disabled={!trimmed} arrow>
        {NAME_QUESTION.cta}
      </CtaButton>
    </form>
  );
}
